/**
 * @fileoverview Connect user-selected upload/download work to backend transfer sessions and artifacts. Local file/progress state is separate from server session identity; explicit completion and cancellation reconcile them. `documentFilePolicy.js` supplies supported format context and `kc-internal-backend/functions/document_transfers.py` owns durable transfer behavior. Aborting browser transport and cancelling persisted work have different boundaries; partial failures must retain recoverable file/session identities.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import api from '../../api'
import AppIcon from '../shared/AppIcon'
import { WORD_DOCUMENT_EXTENSIONS, WORD_DOCUMENT_ONLY_MESSAGE } from './documentFilePolicy'
import './DocumentTransferManager.css'

export const DOCUMENT_TRANSFER_EXTENSIONS = WORD_DOCUMENT_EXTENSIONS

const ACCEPTED_FILES = DOCUMENT_TRANSFER_EXTENSIONS.map(extension => `.${extension}`).join(',')
const DEFAULT_MAX_FILE_BYTES = 250 * 1024 * 1024
let nextTransferLocalId = 0



function extensionOf(filename) {
  const parts = String(filename || '').toLowerCase().split('.')
  return parts.length > 1 ? parts.pop() : ''
}



function uploadRelativePath(file) {
  return String(file.webkitRelativePath || file.relativePath || file.name || '').replace(/\\/g, '/')
}



function displaySize(bytes) {
  const size = Number(bytes || 0)
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(size < 10 * 1024 * 1024 ? 1 : 0)} MB`
}



function errorMessage(error, fallback) {
  return error?.response?.data?.error || error?.message || fallback
}



export function transferApiUrl(path) {
  const value = String(path || '')
  const base = String(api.defaults?.baseURL || '/api').replace(/\/$/, '')
  if (value.startsWith('/api/')) {
    return `${base.replace(/\/api$/, '')}${value}`
  }
  return `${base}/${value.replace(/^\//, '')}`
}



function readDirectoryEntries(reader) {
  return new Promise((resolve, reject) => {
    const entries = []
    const readBatch =

    () => {
      reader.readEntries(batch => {
        if (!batch.length) {
          resolve(entries)
          return
        }
        entries.push(...batch)
        readBatch()
      }, reject)
    }
    readBatch()
  })
}



function entryFile(entry) {
  return new Promise((resolve, reject) => entry.file(resolve, reject))
}



async function filesFromEntry(entry, parentPath = '') {
  const path = [parentPath, entry.name].filter(Boolean).join('/')
  if (entry.isFile) {
    const file = await entryFile(entry)
    Object.defineProperty(file, 'relativePath', { value: path, configurable: true })
    return [file]
  }
  if (!entry.isDirectory) return []
  const children = await readDirectoryEntries(entry.createReader())
  const nested = await Promise.all(children.map(child => filesFromEntry(child, path)))
  return nested.flat()
}



export async function droppedFiles(dataTransfer) {
  const entries = Array.from(dataTransfer?.items || [])
    .map(item => item.webkitGetAsEntry?.())
    .filter(Boolean)
  if (entries.length) {
    const nested = await Promise.all(entries.map(entry => filesFromEntry(entry)))
    return nested.flat()
  }
  return Array.from(dataTransfer?.files || [])
}



export function isExternalFileDrag(dataTransfer) {
  const types = Array.from(dataTransfer?.types || [])
  return types.includes('Files') || Boolean(dataTransfer?.files?.length)
}



function validateFiles(files, maxFileBytes, allowedExtensions = DOCUMENT_TRANSFER_EXTENSIONS, existingPaths = []) {
  const seen = new Set(existingPaths.map(path => String(path || '').toLowerCase()))
  return files.map(

  (file, index) => {
    const relativePath = uploadRelativePath(file)
    const extension = extensionOf(file.name)
    let error = ''
    if (!DOCUMENT_TRANSFER_EXTENSIONS.includes(extension)) {
      error = WORD_DOCUMENT_ONLY_MESSAGE
    } else if (!allowedExtensions.includes(extension)) {
      error = 'This Word format is disabled by the document upload policy'
    } else if (!file.size) {
      error = 'Empty files cannot be uploaded'
    } else if (file.size > maxFileBytes) {
      error = `File exceeds the ${displaySize(maxFileBytes)} limit`
    } else if (seen.has(relativePath.toLowerCase())) {
      error = 'This path appears more than once'
    }
    seen.add(relativePath.toLowerCase())
    return {
      localId: `${Date.now()}-${nextTransferLocalId++}-${index}-${file.name}`,
      serverFileId: '',
      file,
      relativePath,
      progress: 0,
      status: error ? 'invalid' : 'pending',
      error,
    }
  })
}



function statusLabel(entry) {
  if (entry.status === 'uploading') return `${entry.progress}%`
  if (entry.status === 'uploaded') return 'Stored'
  if (entry.status === 'complete') return 'Added'
  if (entry.status === 'skipped') return 'Skipped'
  if (entry.status === 'cancelled') return 'Cancelled'
  if (entry.status === 'failed' || entry.status === 'invalid') return 'Needs attention'
  return 'Ready'
}



function DocumentTransferManager({
  destinationFolderId = '',
  destinationLabel = '',
  matterNumber = '',
  folderPath = '',
  initialFiles = [],
  autoStart = false,
  background = false,
  selectedItems = [],
  onNotice,
  onUploadComplete,
}) {
  const [entries, setEntries] = useState(() => validateFiles(
    Array.from(initialFiles || []),
    DEFAULT_MAX_FILE_BYTES,
    DOCUMENT_TRANSFER_EXTENSIONS,
  ))
  const [conflictPolicy, setConflictPolicy] = useState('keep_both')
  const [sessionId, setSessionId] = useState('')
  const [busy, setBusy] = useState(false)
  const [dragActive, setDragActive] = useState(false)
  const [notice, setNotice] = useState('')
  const [noticeKind, setNoticeKind] = useState('status')
  const [maxFileBytes, setMaxFileBytes] = useState(DEFAULT_MAX_FILE_BYTES)
  const [allowedExtensions, setAllowedExtensions] = useState(DOCUMENT_TRANSFER_EXTENSIONS)
  const [downloadJob, setDownloadJob] = useState(null)
  const controllersRef = useRef(new Map())
  const activeRunRef = useRef(0)
  const mountedRef = useRef(true)
  const fileInputRef = useRef(null)
  const folderInputRef = useRef(null)
  const autoStartedRef = useRef(false)
  const downloadJobId = downloadJob?.job_id
  const downloadJobStatus = downloadJob?.status

  useEffect(() => {
    let live = true
    api.get('/simul/archive/transfers/config')
      .then(response => {
        if (live && response.data?.max_file_bytes) setMaxFileBytes(response.data.max_file_bytes)
        if (live && Array.isArray(response.data?.allowed_extensions)) {
          setAllowedExtensions(response.data.allowed_extensions.map(value => String(value).toLowerCase()).filter(value => DOCUMENT_TRANSFER_EXTENSIONS.includes(value)))
        }
      })
      .catch(() => {})
    return () => { live = false }
  }, [])

  useEffect(() => () => {
    mountedRef.current = false
    activeRunRef.current += 1
    controllersRef.current.forEach(controller => controller.abort())
  }, [])

  useEffect(() => {
    if (!downloadJobId || !['queued', 'running'].includes(downloadJobStatus)) return undefined
    const timer = window.setInterval(async () => {
      try {
        const response = await api.get(`/simul/archive/transfers/downloads/${encodeURIComponent(downloadJobId)}`)
        setDownloadJob(response.data?.download || null)
      } catch (error) {
        setDownloadJob(current => ({ ...current, status: 'failed', error: errorMessage(error, 'Download preparation failed') }))
      }
    }, 1500)
    return () => window.clearInterval(timer)
  }, [downloadJobId, downloadJobStatus])

  const announce = useCallback(

  (text, kind = 'status') => {
    setNoticeKind(kind)
    setNotice(text)
    onNotice?.(text, kind)
  }, [onNotice])

  const addFiles = useCallback(

  files => {
    setEntries(current => {
      const validated = validateFiles(
        Array.from(files || []),
        maxFileBytes,
        allowedExtensions,
        current.map(entry => entry.relativePath),
      )
      return [...current, ...validated]
    })
    announce('')
  }, [allowedExtensions, announce, maxFileBytes])

  const updateEntry = useCallback(

  (localId, patch) => {
    setEntries(current => current.map(entry => entry.localId === localId ? { ...entry, ...patch } : entry))
  }, [])

  const uploadOne = useCallback(

  async (entry, activeSessionId, serverFileId, runId) => {
    if (!serverFileId) {
      updateEntry(entry.localId, { status: 'failed', error: 'The upload session did not include this file' })
      return false
    }
    const controller = new AbortController()
    controllersRef.current.set(entry.localId, controller)
    updateEntry(entry.localId, { status: 'uploading', error: '', progress: 0, serverFileId })
    const body = new FormData()
    body.append('file', entry.file, entry.file.name)
    try {
      const response = await api.post(
        `/simul/archive/transfers/uploads/${encodeURIComponent(activeSessionId)}/files/${encodeURIComponent(serverFileId)}`,
        body,
        {
          signal: controller.signal,
          onUploadProgress: event => {
            if (!event.total) return
            updateEntry(entry.localId, { progress: Math.min(100, Math.round((event.loaded / event.total) * 100)) })
          },
        },
      )
      if (runId !== activeRunRef.current) return false
      const uploaded = response.data?.file || {}
      updateEntry(entry.localId, { status: uploaded.status || 'uploaded', progress: 100, error: '' })
      return true
    } catch (error) {
      const cancelled = controller.signal.aborted || runId !== activeRunRef.current
      updateEntry(entry.localId, {
        status: cancelled ? 'cancelled' : 'failed',
        error: cancelled ? 'Upload cancelled' : errorMessage(error, 'Upload failed'),
      })
      return false
    } finally {
      controllersRef.current.delete(entry.localId)
    }
  }, [updateEntry])

  const finalize = useCallback(

  async activeSessionId => {
    const response = await api.post(`/simul/archive/transfers/uploads/${encodeURIComponent(activeSessionId)}/complete`)
    const upload = response.data?.upload || {}
    setEntries(current => current.map((entry, index) => {
      const serverEntry = (upload.files || [])[index]
      return serverEntry ? {
        ...entry,
        status: serverEntry.status,
        error: serverEntry.error || '',
        progress: serverEntry.status === 'complete' ? 100 : entry.progress,
        documentId: serverEntry.document_id || '',
        revisionId: serverEntry.revision_id || '',
      } : entry
    }))
    if (upload.status === 'complete') {
      announce(`${(upload.files || []).filter(file => file.status === 'complete').length} file${upload.files?.length === 1 ? '' : 's'} added`)
      onUploadComplete?.(upload)
    } else {
      announce('Some files could not be added. Review them and retry.', 'alert')
    }
    return upload
  }, [announce, onUploadComplete])

  const startUpload = useCallback(

  async () => {
    const pending = entries.filter(entry => entry.status === 'pending')
    if (!pending.length || entries.some(entry => entry.status === 'invalid')) return
    setBusy(true)
    announce('')
    const runId = activeRunRef.current + 1
    activeRunRef.current = runId
    try {
      const response = await api.post('/simul/archive/transfers/uploads', {
        folder_id: destinationFolderId,
        matter_number: matterNumber,
        folder_path: folderPath,
        conflict_policy: conflictPolicy,
        files: pending.map(entry => ({
          name: entry.file.name,
          relative_path: entry.relativePath,
          size: entry.file.size,
          mime_type: entry.file.type || 'application/octet-stream',
        })),
      })
      const upload = response.data?.upload
      const activeSessionId = upload?.session_id
      if (!activeSessionId) throw new Error('Upload session was not created')
      setSessionId(activeSessionId)
      const serverFiles = upload.files || []
      let cursor = 0
      let succeeded = true
      const workers = Array.from({ length: Math.min(3, pending.length) }, async () => {
        while (cursor < pending.length) {
          const index = cursor
          cursor += 1
          const entry = pending[index]
          const ok = await uploadOne(entry, activeSessionId, serverFiles[index]?.file_id, runId)
          if (!ok) succeeded = false
        }
      })
      await Promise.all(workers)
      if (runId !== activeRunRef.current) return
      if (succeeded) await finalize(activeSessionId)
      else announce('Some uploads stopped. Retry the affected files or cancel the batch.', 'alert')
    } catch (error) {
      if (runId === activeRunRef.current) announce(errorMessage(error, 'Could not start the upload'), 'alert')
    } finally {
      if (mountedRef.current && runId === activeRunRef.current) setBusy(false)
    }
  }, [announce, conflictPolicy, destinationFolderId, entries, finalize, folderPath, matterNumber, uploadOne])

  useEffect(

  () => {
    if (!autoStart || autoStartedRef.current || entries.length === 0) return
    autoStartedRef.current = true
    if (entries.some(entry => entry.status === 'invalid')) {
      announce(entries.find(entry => entry.status === 'invalid').error, 'alert')
      return
    }
    startUpload()
  }, [announce, autoStart, entries, startUpload])

  const retryEntry = useCallback(

  async entry => {
    if (!sessionId || !entry.serverFileId) return
    setBusy(true)
    const runId = activeRunRef.current + 1
    activeRunRef.current = runId
    const succeeded = await uploadOne(entry, sessionId, entry.serverFileId, runId)
    if (succeeded) {
      try {
        const response = await api.get(`/simul/archive/transfers/uploads/${encodeURIComponent(sessionId)}`)
        const upload = response.data?.upload
        if ((upload?.files || []).every(file => ['uploaded', 'complete', 'skipped'].includes(file.status))) {
          await finalize(sessionId)
        }
      } catch (error) {
        announce(errorMessage(error, 'The file uploaded, but the batch could not be completed'), 'alert')
      }
    }
    setBusy(false)
  }, [announce, finalize, sessionId, uploadOne])

  const cancelUpload = useCallback(

  async () => {
    activeRunRef.current += 1
    controllersRef.current.forEach(controller => controller.abort())
    if (sessionId) {
      try {
        await api.delete(`/simul/archive/transfers/uploads/${encodeURIComponent(sessionId)}`)
      } catch (error) {
        announce(errorMessage(error, 'Could not cancel the upload session'), 'alert')
        return
      }
    }
    setEntries(current => current.map(entry => (
      ['complete', 'skipped'].includes(entry.status) ? entry : { ...entry, status: 'cancelled', error: '' }
    )))
    setBusy(false)
    announce('Upload cancelled')
  }, [announce, sessionId])

  const removeEntry =

  localId => {
    if (sessionId) return
    setEntries(current => current.filter(entry => entry.localId !== localId))
  }

  const handleDrop =

  async event => {
    event.preventDefault()
    setDragActive(false)
    addFiles(await droppedFiles(event.dataTransfer))
  }

  const downloadSelection =

  async () => {
    const documentIds = []
    const folderIds = []
    selectedItems.forEach(item => {
      if (item.item_type === 'folder' || (item.folder_id && !item.document_id)) folderIds.push(item.folder_id || item.id)
      else if (item.document_id || item.id) documentIds.push(item.document_id || item.id)
    })
    setDownloadJob({ status: 'queueing', job_id: '', archive_name: 'documents.zip' })
    try {
      const response = await api.post('/simul/archive/transfers/downloads', {
        document_ids: documentIds,
        folder_ids: folderIds,
      })
      setDownloadJob(response.data?.download || null)
    } catch (error) {
      setDownloadJob({ status: 'failed', error: errorMessage(error, 'Could not prepare the download') })
    }
  }

  const hasInvalid = entries.some(entry => entry.status === 'invalid')
  const pendingCount = entries.filter(entry => entry.status === 'pending').length
  const canStart = pendingCount > 0 && !hasInvalid && !busy && !sessionId
  const selectedCount = selectedItems.length
  const summary = useMemo(() => {
    if (!entries.length) return 'Word documents and templates: DOC, DOCX, DOCM, DOT, DOTX, DOTM'
    return `${entries.length} selected · ${displaySize(entries.reduce((total, entry) => total + entry.file.size, 0))}`
  }, [entries])
  const acceptedFiles = allowedExtensions.map(extension => `.${extension}`).join(',') || ACCEPTED_FILES

  if (background) return null

  return (
    <section className="document-transfer-manager" aria-label="File transfers">
      <div className="document-transfer-heading">
        <div>
          <h3>Upload files</h3>
          <p>{summary}</p>
          {destinationLabel && (
            <p className="document-transfer-destination">
              <AppIcon icon="pi-folder" size={14} aria-hidden="true" />
              Uploading to <strong>{destinationLabel}</strong>
            </p>
          )}
        </div>
        {entries.length > 0 && !sessionId && (
          <button type="button" className="document-transfer-clear" onClick={() => setEntries([])}>Clear</button>
        )}
      </div>

      <div
        className={`document-transfer-dropzone${dragActive ? ' is-active' : ''}`}
        onDragEnter={event => { event.preventDefault(); setDragActive(true) }}
        onDragOver={event => event.preventDefault()}
        onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget)) setDragActive(false) }}
        onDrop={handleDrop}
      >
        <AppIcon icon="pi-upload" size={24} aria-hidden="true" />
        <strong>Drop Word documents or folders here</strong>
        <span>Store PDFs, images, and other files in Google Drive.</span>
        <div className="document-transfer-pickers">
          <button type="button" onClick={() => fileInputRef.current?.click()}>Choose files</button>
          <button type="button" onClick={() => folderInputRef.current?.click()}>Choose folder</button>
        </div>
        <input
          ref={fileInputRef}
          className="document-transfer-hidden-input"
          type="file"
          aria-label="Choose files to upload"
          accept={acceptedFiles}
          multiple
          onChange={event => { addFiles(event.target.files); event.target.value = '' }}
        />
        <input
          ref={folderInputRef}
          className="document-transfer-hidden-input"
          type="file"
          aria-label="Choose a folder to upload"
          accept={acceptedFiles}
          multiple
          webkitdirectory=""
          directory=""
          onChange={event => { addFiles(event.target.files); event.target.value = '' }}
        />
      </div>

      {entries.length > 0 && (
        <div className="document-transfer-queue" role="list" aria-label="Upload queue">
          {entries.map(entry => (
            <div className={`document-transfer-file is-${entry.status}`} key={entry.localId} role="listitem">
              <span className="document-transfer-file-icon" aria-hidden="true">{entry.status === 'complete' ? '✓' : '↥'}</span>
              <span className="document-transfer-file-main">
                <strong>{entry.relativePath}</strong>
                <small>{entry.error || displaySize(entry.file.size)}</small>
                {entry.status === 'uploading' && (
                  <span
                    className="document-transfer-progress"
                    role="progressbar"
                    aria-label={`${entry.relativePath} upload progress`}
                    aria-valuemin="0"
                    aria-valuemax="100"
                    aria-valuenow={entry.progress}>
                    <span style={{ width: `${entry.progress}%` }} />
                  </span>
                )}
              </span>
              <span className="document-transfer-status">{statusLabel(entry)}</span>
              {entry.status === 'failed' && (
                <button type="button" onClick={() => retryEntry(entry)} disabled={busy}>Retry</button>
              )}
              {!sessionId && (
                <button type="button" className="document-transfer-remove" onClick={() => removeEntry(entry.localId)} aria-label={`Remove ${entry.relativePath}`}>×</button>
              )}
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && (
        <div className="document-transfer-actions">
          {!sessionId && (
            <label>
              If a name already exists
              <select value={conflictPolicy} onChange={event => setConflictPolicy(event.target.value)}>
                <option value="keep_both">Keep both</option>
                <option value="replace">Replace with a new version</option>
                <option value="skip">Skip it</option>
              </select>
            </label>
          )}
          <span className="document-transfer-action-buttons">
            {sessionId && entries.some(entry => !['complete', 'skipped'].includes(entry.status)) && (
              <button type="button" className="is-secondary" onClick={cancelUpload}>Cancel</button>
            )}
            <button type="button" className="is-primary" onClick={startUpload} disabled={!canStart}>
              {busy ? 'Uploading…' : pendingCount > 0 ? `Upload ${pendingCount} file${pendingCount === 1 ? '' : 's'}` : 'Upload files'}
            </button>
          </span>
        </div>
      )}

      {notice && <p className={`document-transfer-notice is-${noticeKind}`} role={noticeKind}>{notice}</p>}

      <div className="document-transfer-download">
        <div>
          <h3>Download selection</h3>
          <p>{selectedCount ? `${selectedCount} selected item${selectedCount === 1 ? '' : 's'}` : 'Select files or folders to create a ZIP.'}</p>
        </div>
        <button type="button" onClick={downloadSelection} disabled={!selectedCount || ['queueing', 'queued', 'running'].includes(downloadJob?.status)}>
          <AppIcon icon="pi-download" size={17} aria-hidden="true" />
          Prepare ZIP
        </button>
      </div>

      {downloadJob && (
        <div className={`document-transfer-download-status is-${downloadJob.status}`} role={downloadJob.status === 'failed' ? 'alert' : 'status'}>
          {['queueing', 'queued', 'running'].includes(downloadJob.status) && (
            <span>Preparing {downloadJob.archive_name || 'documents.zip'}{downloadJob.document_count ? ` · ${downloadJob.processed_count || 0} of ${downloadJob.document_count}` : ''}</span>
          )}
          {downloadJob.status === 'ready' && (
            <a href={transferApiUrl(downloadJob.download_url)} download={downloadJob.archive_name}>Download {downloadJob.archive_name}</a>
          )}
          {downloadJob.status === 'failed' && <span>{downloadJob.error || 'Download preparation failed'}</span>}
        </div>
      )}
    </section>
  )
}

export default DocumentTransferManager
